(() => {
  const init = (sectionRoot) => {
    if (!sectionRoot || sectionRoot.dataset.rbGridBound === 'true') return;
    sectionRoot.dataset.rbGridBound = 'true';

  const modal = sectionRoot.querySelector('[data-sc-quickview-modal]');
  const modalTitle = sectionRoot.querySelector('[data-sc-quickview-title]');
  const modalOpen = sectionRoot.querySelector('[data-sc-quickview-open]');
  const modalImage = sectionRoot.querySelector('[data-sc-quickview-image]');
  const modalVendor = sectionRoot.querySelector('[data-sc-quickview-vendor]');
  const modalName = sectionRoot.querySelector('[data-sc-quickview-name]');
  const modalPrice = sectionRoot.querySelector('[data-sc-quickview-price]');
  const modalDescription = sectionRoot.querySelector('[data-sc-quickview-description]');
  const modalAddForm = sectionRoot.querySelector('[data-sc-quickview-add-form]');
  const modalVariantInput = sectionRoot.querySelector('[data-sc-quickview-variant-id]');
  const modalAddButton = sectionRoot.querySelector('[data-sc-quickview-add-button]');
  const closeButtons = sectionRoot.querySelectorAll('[data-sc-quickview-close]');
  const toast = sectionRoot.querySelector('[data-sc-card-toast]');
  const wishlistButtons = sectionRoot.querySelectorAll('[data-sc-wishlist]');
  const quickViewButtons = sectionRoot.querySelectorAll('[data-sc-quick-view]');
  const addForms = sectionRoot.querySelectorAll('[data-sc-card-add-form]');
  const WISHLIST_KEY = 'sc_wishlist_products_v1';
  const EMPTY_IMAGE =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  const productCache = new Map();
  let toastTimer;

  const showToast = (text) => {
    if (!toast) return;
    toast.textContent = text;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 1600);
  };

  const readWishlist = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map((v) => String(v)) : [];
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (list) => {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      window.dispatchEvent(new Event('sc:wishlist-updated'));
    } catch (error) {
      // no-op
    }
  };

  const syncWishlist = () => {
    const list = new Set(readWishlist());
    wishlistButtons.forEach((button) => {
      const id = String(button.getAttribute('data-product-id') || '');
      const active = !!id && list.has(id);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  };

  const getCurrencyCode = () => {
    try {
      const code = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
        ? window.Shopify.currency.active
        : '';
      if (code) return String(code).toUpperCase();
    } catch (error) {
      // no-op
    }
    return 'USD';
  };

  const formatMoneyFromCents = (rawCents, fallback = '') => {
    const cents = Number(rawCents);
    if (!Number.isFinite(cents)) return fallback;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: getCurrencyCode(),
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (error) {
      return fallback || `${(cents / 100).toFixed(2)}`;
    }
  };

  const toProductJsonUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      const pathname = parsed.pathname.replace(/\/+$/, '');
      if (!pathname.startsWith('/products/')) return '';
      return `${pathname}.js`;
    } catch (error) {
      return '';
    }
  };

  const stripHtmlToExcerpt = (html, limit = 220) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(String(html), 'text/html');
      const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) return '';
      return text.length > limit ? `${text.slice(0, limit).trim()}...` : text;
    } catch (error) {
      const fallback = String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return fallback.length > limit ? `${fallback.slice(0, limit).trim()}...` : fallback;
    }
  };

  const fetchProductPayload = async (url) => {
    const jsonUrl = toProductJsonUrl(url);
    if (!jsonUrl) return null;
    if (productCache.has(jsonUrl)) return productCache.get(jsonUrl);
    const response = await fetch(`${jsonUrl}?_=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Quick view fetch failed');
    const payload = await response.json();
    productCache.set(jsonUrl, payload);
    return payload;
  };

  const renderQuickView = (payload) => {
    const title = String(payload?.title || 'Product quick view');
    const url = String(payload?.url || '#');
    const vendor = String(payload?.vendor || '').trim();
    const price = String(payload?.price || '').trim();
    const image = String(payload?.image || '').trim();
    const variantId = String(payload?.variantId || '').trim();
    const available = payload?.available !== false;
    const description = String(payload?.description || '').trim();

    if (modalTitle) modalTitle.textContent = title;
    if (modalName) modalName.textContent = title;
    if (modalOpen) modalOpen.setAttribute('href', url || '#');
    if (modalVendor) {
      modalVendor.textContent = vendor;
      modalVendor.hidden = !vendor;
    }
    if (modalPrice) {
      modalPrice.textContent = price || 'See product details';
    }
    if (modalDescription) {
      modalDescription.textContent = description || 'Review key details and add this item directly from quick view.';
    }
    if (modalImage) {
      modalImage.setAttribute('src', image || EMPTY_IMAGE);
      modalImage.setAttribute('alt', title);
    }
    if (modalVariantInput) {
      modalVariantInput.value = variantId;
    }
    if (modalAddButton instanceof HTMLButtonElement) {
      modalAddButton.disabled = !variantId || !available;
      modalAddButton.textContent = available ? 'Add to cart' : 'Sold out';
    }
  };

  const closeModal = () => {
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove('sc-no-scroll');
  };

  const openModalFromButton = async (button) => {
    if (!modal) return;
    const basePayload = {
      url: button.getAttribute('data-product-url') || '#',
      title: button.getAttribute('data-product-title') || 'Product quick view',
      image: button.getAttribute('data-product-image') || '',
      vendor: button.getAttribute('data-product-vendor') || '',
      price: button.getAttribute('data-product-price') || '',
      variantId: button.getAttribute('data-product-variant-id') || '',
      available: button.getAttribute('data-product-available') !== 'false',
      description: '',
    };
    renderQuickView(basePayload);
    modal.removeAttribute('hidden');
    document.documentElement.classList.add('sc-no-scroll');

    try {
      const payload = await fetchProductPayload(basePayload.url);
      if (!payload) return;
      const variants = Array.isArray(payload.variants) ? payload.variants : [];
      const chosen = variants.find((variant) => Boolean(variant?.available)) || variants[0] || null;
      const priceValue =
        chosen && Number.isFinite(Number(chosen.price))
          ? formatMoneyFromCents(chosen.price, basePayload.price)
          : formatMoneyFromCents(payload.price, basePayload.price);
      const compareValue = chosen && Number.isFinite(Number(chosen.compare_at_price))
        ? Number(chosen.compare_at_price)
        : Number(payload.compare_at_price || 0);
      const comparePrice = compareValue > 0 ? formatMoneyFromCents(compareValue, '') : '';
      renderQuickView({
        url: basePayload.url,
        title: payload.title || basePayload.title,
        image: payload.featured_image || basePayload.image,
        vendor: payload.vendor || basePayload.vendor,
        price: comparePrice && priceValue ? `${priceValue} (was ${comparePrice})` : (priceValue || basePayload.price),
        variantId: chosen && chosen.id ? String(chosen.id) : basePayload.variantId,
        available: chosen ? Boolean(chosen.available) : basePayload.available,
        description: stripHtmlToExcerpt(payload.description || payload.body_html || ''),
      });
    } catch (error) {
      // Keep fallback content when product JSON is unavailable.
    }
  };

  closeButtons.forEach((button) => {
    button.addEventListener('click', closeModal);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal && !modal.hasAttribute('hidden')) {
      closeModal();
    }
  });
  window.addEventListener('sc:wishlist-updated', syncWishlist);

  wishlistButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const id = String(button.getAttribute('data-product-id') || '');
      if (!id) return;
      const title = String(button.getAttribute('data-product-title') || 'Product');
      const list = readWishlist();
      const idx = list.indexOf(id);
      if (idx >= 0) {
        list.splice(idx, 1);
        writeWishlist(list);
        syncWishlist();
        showToast(`${title} removed from wishlist`);
        return;
      }
      list.push(id);
      writeWishlist(list);
      syncWishlist();
      showToast(`${title} added to wishlist`);
    });
  });

  quickViewButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openModalFromButton(button);
    });
  });

  const cartSectionsToRender = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return [];
    if (typeof drawer.getSectionsToRender === 'function') {
      try { return drawer.getSectionsToRender().map((s) => s.id || s.section).filter(Boolean); } catch (_) {}
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  };

  const submitCartAdd = async (form) => {
    const drawer = document.querySelector('cart-drawer');
    const sectionList = cartSectionsToRender();
    const formData = new FormData(form);
    if (drawer && sectionList.length) {
      formData.append('sections', sectionList.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData,
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Add to cart failed');
    return response.json();
  };

  const openCartDrawerWithResponse = (parsedState, triggerEl) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // Manual update path: replace inner content of #CartDrawer + #cart-icon-bubble,
    // strip is-empty everywhere, then add active+animate. Dawn's renderContents
    // method has been observed to leave drawer in a half-broken state on some
    // theme configurations, so we don't rely on it.
    if (parsedState && parsedState.sections) {
      if (parsedState.sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const freshInner = doc.querySelector('#CartDrawer');
        const targetInner = drawer.querySelector('#CartDrawer');
        if (freshInner && targetInner) {
          targetInner.innerHTML = freshInner.innerHTML;
        }
        const freshOuter = doc.querySelector('cart-drawer');
        if (freshOuter && freshOuter.className) {
          drawer.className = freshOuter.className;
        }
      }
      if (parsedState.sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const inner = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
          bubble.innerHTML = inner ? inner.innerHTML : parsedState.sections['cart-icon-bubble'];
        }
      }
    }
    drawer.classList.remove('is-empty');
    const innerWrap = drawer.querySelector('.drawer__inner');
    if (innerWrap) innerWrap.classList.remove('is-empty');
    const itemsWrap = drawer.querySelector('cart-drawer-items');
    if (itemsWrap) itemsWrap.classList.remove('is-empty');
    if (triggerEl && typeof drawer.setActiveElement === 'function') {
      try { drawer.setActiveElement(triggerEl); } catch (_) {}
    }
    requestAnimationFrame(() => {
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function' && !overlay.dataset.scBound) {
        overlay.addEventListener('click', () => drawer.close());
        overlay.dataset.scBound = '1';
      }
    });
  };

  addForms.forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;
      const defaultLabel = button.textContent || 'Add to cart';
      button.disabled = true;
      button.textContent = 'Adding...';
      try {
        const parsedState = await submitCartAdd(form);
        showToast('Added to cart');
        openCartDrawerWithResponse(parsedState, button);
      } catch (error) {
        form.submit();
        return;
      }
      button.textContent = 'Added';
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = defaultLabel;
      }, 900);
    });
  });

  if (modalAddForm) {
    modalAddForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!(modalAddButton instanceof HTMLButtonElement) || !modalVariantInput || !modalVariantInput.value) return;
      const defaultLabel = modalAddButton.textContent || 'Add to cart';
      modalAddButton.disabled = true;
      modalAddButton.textContent = 'Adding...';
      try {
        const parsedState = await submitCartAdd(modalAddForm);
        showToast('Added to cart');
        modalAddButton.textContent = 'Added';
        openCartDrawerWithResponse(parsedState, modalAddButton);
        window.setTimeout(() => {
          modalAddButton.disabled = false;
          modalAddButton.textContent = defaultLabel;
          closeModal();
        }, 800);
      } catch (error) {
        modalAddButton.disabled = false;
        modalAddButton.textContent = defaultLabel;
        if (modalOpen && modalOpen.getAttribute('href')) {
          window.location.href = modalOpen.getAttribute('href');
        }
      }
    });
  }

  syncWishlist();

  };
  const boot = () => document.querySelectorAll('.sc-product-grid').forEach(e => init(e.closest('.shopify-section')));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once:true}); else boot();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
